import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOMetadata = ({ 
  title,
  description,
  keywords,
  canonical,
  ogImage = '/social-share.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData = null,
  noIndex = false
}) => {
  const baseUrl = 'https://progresscraft.vercel.app';
  const fullTitle = title ? `${title} | ProgressCraft` : '🕉️ ProgressCraft - Spiritual Journey Companion';
  const fullCanonical = canonical ? `${baseUrl}${canonical}` : baseUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="ProgressCraft" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Predefined SEO configurations for different apps
export const seoConfigs = {
  home: {
    title: '🕉️ Spiritual Journey Companion | Prayer Counter & Meditation Apps',
    description: 'Track your spiritual journey with ProgressCraft. Beautiful prayer counter, parayana tracker, and meditation apps. Offline support, cloud sync, and detailed analytics.',
    keywords: 'prayer counter, parayana tracker, meditation app, spiritual journey, mantra counter, temple visits, spiritual practice, PWA, offline app',
    canonical: '/',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "ProgressCraft",
      "description": "Spiritual journey companion with prayer counter, parayana tracker, and meditation apps",
      "applicationCategory": "LifestyleApplication",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "featureList": [
        "Prayer Counter",
        "Parayana Tracker", 
        "Random Counter",
        "Offline Support",
        "Cloud Sync",
        "Analytics Dashboard"
      ]
    }
  },
  
  prayerCounter: {
    title: '🕉️ Prayer Counter & Parayana Tracker',
    description: 'Track your daily prayers, mantras, and parayanas with beautiful analytics. Offline support, cloud sync, and detailed progress tracking for your spiritual practice.',
    keywords: 'prayer counter, parayana tracker, mantra counting, spiritual practice, Hindu prayers, Buddhist meditation, daily prayers, offline prayer app',
    canonical: '/apps/prayer-counter',
    ogImage: '/prayer-counter-share.png',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      "name": "Prayer Counter - ProgressCraft",
      "description": "Track daily prayers, mantras, and parayanas with analytics",
      "applicationCategory": "LifestyleApplication",
      "operatingSystem": "Any",
      "featureList": [
        "Prayer Counting",
        "Parayana Tracking",
        "Progress Analytics",
        "Offline Support",
        "Cloud Sync"
      ]
    }
  },
  
  randomCounter: {
    title: '🔢 Random Counter App | General Purpose Counting Tool',
    description: 'Beautiful, intuitive counter app for counting anything. Multiple counters, custom names, cloud sync, and offline support. Perfect for exercise, habits, or any counting needs.',
    keywords: 'counter app, counting tool, habit tracker, exercise counter, general counter, multiple counters, offline counter, cloud sync counter',
    canonical: '/apps/random-counter',
    ogImage: '/random-counter-share.png',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      "name": "Random Counter - ProgressCraft",
      "description": "General purpose counting tool with multiple counters and cloud sync",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Any",
      "featureList": [
        "Multiple Counters",
        "Custom Counter Names",
        "Cloud Sync",
        "Offline Support",
        "Beautiful UI"
      ]
    }
  },
  
  analytics: {
    title: '📊 Spiritual Analytics & Progress Tracking',
    description: 'View detailed analytics of your spiritual practice. Track prayer progress, view weekly charts, and monitor your spiritual journey with beautiful visualizations.',
    keywords: 'spiritual analytics, prayer statistics, meditation progress, spiritual progress tracking, prayer analytics, meditation statistics',
    canonical: '/apps/prayer-counter/analytics',
    ogImage: '/analytics-share.png'
  }
};

export default SEOMetadata; 